
import { Box, Paper, Table, TableBody, TableContainer, TableHead, TablePagination, Typography, TextField, Button, InputAdornment } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import styles from '../../../styles.module.css';
import { useDispatch } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
    Breadcrumb,
    CustomePopover,
    DeleteDialog,
    HandleSort,
    StyledTableCell,
    StyledTableRow,
    DateConversionOnEntryPage,
} from "../../reusableComponent/Reusables";
import * as XLSX from "xlsx";
import accountManagerMasterService from "../../../services/masters/accountManagerMaster.Service";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../reusableComponent/CustomPagination";

const AccountManagerList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const login_details = sessionStorage.getItem('loginUser');
    const userD = JSON.parse(login_details);
    const userInfo = userD.userInfo;

    // Local state
    const [isDelete, setIsDelete] = useState(false);
    const [deleteInfo, setDeleteInfo] = useState();
    const [sortBy, setSortBy] = useState(null);
    const [defaultRowsPerPage, setDefaultRowsPerPage] = useState(15);
    const [rows, setRows] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [fileUploadError, setFileUploadError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [total_count, setTotalCount] = useState(null);

    useEffect(() => {
        setTotalCount(rows[0]?.totalCount);
    }, [rows]);

    const [controller, setController] = useState({
        page: 0,
        rowsPerPage: defaultRowsPerPage,
    });

    useEffect(() => {
        setController((prev) => ({
            ...prev,
            rowsPerPage: defaultRowsPerPage,
            page: 0,
        }));
    }, [defaultRowsPerPage]);

    const fields = ["sales_officer_name", "mobile_no", "email_id", "created_at"];
    const labels = ["Sales Officer Name", "Mobile No", "Email ID", "Created At"];

    const fetchData = async () => {
        try {
            const params = {
                page: controller.page,
                size: controller.rowsPerPage,
                searchKey: searchKey,
            };
            const response = await accountManagerMasterService.getAccountManagerList(params);
            setRows(response || []);
            setTotalCount(response?.total_count || 0);
            setDefaultRowsPerPage(response?.rows_per_page || 15);
        } catch (error) {
            setSuccessMessage("Error fetching data");
        }
    };

    const handleChangeRowsPerPage = (event) => {
        setController({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
    };

    useEffect(() => {
        fetchData();
    }, [controller, searchKey]);

    const handleSearch = (e) => {
        setSearchKey(e.target.value);
        setController((prev) => ({ ...prev, page: 0 }));
    };

    const handleChangePage = (event, newPage) => {
        setController({ ...controller, page: newPage });
    };

    const deleteRecord = async (id) => {
        try {
            await accountManagerMasterService.editDeleteAccountManager({
                sales_officer_id: id,
                changedUpdatedValue: "delete",
            });
            setIsDelete(false);
            fetchData();
            setSuccessMessage("Record deleted successfully");
        } catch (error) {
            setSuccessMessage("Error deleting record");
        }
    };

    const handleDelete = (functions) => {
        setIsDelete(true);
        setDeleteInfo(functions);
    };

    const handleClose = () => {
        setIsDelete(false);
        setDeleteInfo(null);
    };

    const handleImportExcel = (e) => {
        const file = e.target.files[0];
        setFileUploadError("");
        if (!file) { setFileUploadError("No file selected."); return; }

        const fileExtension = file.name.split(".").pop().toLowerCase();
        if (!["xlsx", "xls", "csv"].includes(fileExtension)) {
            setFileUploadError("Invalid file format. Please upload a .xlsx, .xls, or .csv file.");
            return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const worksheetNames = workbook.SheetNames;
                if (worksheetNames.length === 0) throw new Error("The file contains no worksheets.");

                const worksheet = workbook.Sheets[worksheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                if (jsonData.length === 0) throw new Error("The file is empty.");

                const expectedHeaders = ["Sales Officer Name", "Mobile No", "Email ID"];
                const headers = jsonData[0];
                const missingHeaders = expectedHeaders.filter((h) => !headers.includes(h));
                if (missingHeaders.length > 0) throw new Error(`Missing required headers: ${missingHeaders.join(", ")}.`);

                const processedData = jsonData.slice(1).map((row) => ({
                    sales_officer_name: row[headers.indexOf("Sales Officer Name")] || "",
                    mobile_no: row[headers.indexOf("Mobile No")] || "",
                    email_id: row[headers.indexOf("Email ID")] || "",
                }));

                if (processedData.length === 0) throw new Error("The file contains no valid data rows.");

                await dispatch({ type: "ADD_ACCOUNT_MANAGER_CSV", payload: { account_managers: processedData } }).unwrap();
                setSuccessMessage(`Successfully imported ${processedData.length} records.`);
                fetchData();
            } catch (error) {
                setFileUploadError(error.message || "Failed to process the file.");
            }
            e.target.value = null;
        };
        reader.onerror = () => setFileUploadError("Error reading the file. Please try again.");
        reader.readAsArrayBuffer(file);
    };

    const sortedRows = useMemo(() => {
        return [...rows].sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
            const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
            return dateB - dateA;
        });
    }, [rows]);

    return (
        <Box className={`${styles.cts_page}`} sx={{ padding: "0px !important", margin: "0px !important" }}>
            <Box className={`${styles.body}`}>
                <Breadcrumb routeSegments={[{ name: "Sales Officer List" }]} />

                {/* ── Title ── */}
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, px: 1 }}>
                    SALES OFFICER LIST
                </Typography>

                {/* ── Toolbar: Export | Search ... | + Add New  ── */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 2,
                        px: 1,
                    }}
                >
                    {/* Export button — green with icon, matches Depot Master List */}
                    <Button
                        variant="contained"
                        startIcon={<UploadFileIcon />}
                        onClick={() => {
                            const ws = XLSX.utils.json_to_sheet(
                                sortedRows.map((row) =>
                                    labels.reduce((acc, label, i) => {
                                        acc[label] = row[fields[i]] || "";
                                        return acc;
                                    }, {})
                                )
                            );
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, "Sales Officer List");
                            XLSX.writeFile(wb, "Sales Officer List.xlsx");
                        }}
                        sx={{
                            backgroundColor: "#4caf50",
                            color: "#fff",
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            fontWeight: 500,
                            px: 2.5,
                            "&:hover": { backgroundColor: "#388e3c" },
                        }}
                    >
                        Export
                    </Button>

                    {/* Wide search bar — fills remaining space */}
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search..."
                        value={searchKey}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flex: 1,
                            backgroundColor: "#fff",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "4px",
                            },
                        }}
                    />

                    {/* Add New button */}
                    <Button
                        variant="contained"
                        onClick={() => navigate("/masters/add-account-manager")}
                        sx={{
                            backgroundColor: "#1565c0",
                            color: "#fff",
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            "&:hover": { backgroundColor: "#0d47a1" },
                        }}
                    >
                        + Add New
                    </Button>
                </Box>

                {/* ── Table ── */}
                <Paper sx={{ width: '100%' }}>
                    <TableContainer component={Paper} className={`${styles.list_tbl_container}`}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell className={`${styles.table_head}`} onClick={() => HandleSort('sales_officer_name', sortBy, setSortBy, rows)}>
                                        Sales Officer
                                        <span className={`${styles.sort_icon}`}> {sortBy === 'sales_officer_name' ? '▲' : '▼'} </span>
                                    </StyledTableCell>
                                    <StyledTableCell className={`${styles.table_head}`} onClick={() => HandleSort('mobile_no', sortBy, setSortBy, rows)}>
                                        Mobile No
                                        <span className={`${styles.sort_icon}`}> {sortBy === 'mobile_no' ? '▲' : '▼'} </span>
                                    </StyledTableCell>
                                    <StyledTableCell className={`${styles.table_head}`} onClick={() => HandleSort('email_id', sortBy, setSortBy, rows)}>
                                        Email ID
                                        <span className={`${styles.sort_icon}`}> {sortBy === 'email_id' ? '▲' : '▼'} </span>
                                    </StyledTableCell>
                                    <StyledTableCell className={`${styles.table_head}`} onClick={() => HandleSort('created_at', sortBy, setSortBy, rows)}>
                                        Created At
                                        <span className={`${styles.sort_icon}`}> {sortBy === 'created_at' ? '▲' : '▼'} </span>
                                    </StyledTableCell>
                                    <StyledTableCell align="center" className={`${styles.table_head}`}>
                                        Action
                                    </StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody sx={{ backgroundColor: '#fff' }}>
                                {Array.isArray(rows) && rows.map((row) => (
                                    <StyledTableRow key={row.sales_officer_id}>
                                        <StyledTableCell className={`${styles.list_table_body}`}>
                                            {row.sales_officer_name || "N/A"}
                                        </StyledTableCell>
                                        <StyledTableCell className={`${styles.list_table_body}`}>
                                            {row.mobile_no || "N/A"}
                                        </StyledTableCell>
                                        <StyledTableCell className={`${styles.list_table_body}`}>
                                            {row.email_id || "N/A"}
                                        </StyledTableCell>
                                        <StyledTableCell className={`${styles.list_table_body}`}>
                                            {row.created_at ? DateConversionOnEntryPage(row.created_at) : "N/A"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <CustomePopover
                                                row={row}
                                                handleDelete={handleDelete}
                                                linkTo="/masters/add-account-manager"
                                                handlePrint={false}
                                            />
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                <CustomPagination
  page={controller.page}
  rowsPerPage={controller.rowsPerPage}
  totalCount={total_count || 0}
  onPageChange={(newPage) => handleChangePage(null, newPage)}
/>
                </Paper>

                <DeleteDialog
                    open={isDelete}
                    handleClose={handleClose}
                    deleteInfo={deleteInfo}
                    deleteRecord={deleteRecord}
                    deleteID={deleteInfo?.sales_officer_id || null}
                    displayName={deleteInfo?.sales_officer_name || ''}
                />

                <ReusableSnackbar
                    open={!!successMessage || !!fileUploadError}
                    onClose={(event, reason) => {
                        if (reason !== 'clickaway') {
                            setSuccessMessage("");
                            setFileUploadError("");
                        }
                    }}
                    message={successMessage || fileUploadError}
                    autoHideDuration={2000}
                    vertical="top"
                    horizontal="right"
                />
            </Box>
        </Box>
    );
};

export default AccountManagerList;